import { createContext, useContext, useState, useEffect } from 'react';
import apiClient from '../config/axios';

const BranchContext = createContext();

export const useBranch = () => {
  const context = useContext(BranchContext);
  if (!context) {
    throw new Error('useBranch must be used within BranchProvider');
  }
  return context;
};

export const BranchProvider = ({ children }) => {
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);

  // Load selected branch from localStorage on mount
  useEffect(() => {
    const savedBranch = localStorage.getItem('selectedBranch');
    if (savedBranch) {
      try {
        setSelectedBranch(JSON.parse(savedBranch));
      } catch (e) {
        console.error('Error parsing saved branch:', e);
      }
    }
  }, []);

  // Fetch branches for the user
  useEffect(() => {
    fetchBranches();
  }, []);

  const fetchBranches = async () => {
    try {
      setLoading(true);
      // Fetch user data to get branches
      const response = await apiClient.get('/auth/me');
      const user = response.data;
      
      // If user has branches, use them; otherwise create a default list
      if (user.branches && user.branches.length > 0) {
        setBranches(user.branches);
      } else if (user.branch) {
        // Single branch user
        const branchData = typeof user.branch === 'string' 
          ? { _id: user.branch, name: 'Main Branch' }
          : user.branch;
        setBranches([branchData]);
      } else {
        // Create default branches from user data or use a fallback
        // For now, we'll create a default branch based on user info
        const defaultBranch = {
          _id: user._id || 'default',
          name: user.name || 'Main Branch',
          subtitle: 'Fast food restaurant take away',
          image: null,
          isWebPOS: true
        };
        setBranches([defaultBranch]);
      }
    } catch (error) {
      console.error('Error fetching branches:', error);
      // Fallback: create a default branch
      const defaultBranch = {
        _id: 'default',
        name: 'Main Branch',
        subtitle: 'Fast food restaurant take away',
        image: null,
        isWebPOS: true
      };
      setBranches([defaultBranch]);
    } finally {
      setLoading(false);
    }
  };

  const selectBranch = (branch) => {
    setSelectedBranch(branch);
    localStorage.setItem('selectedBranch', JSON.stringify(branch));
    localStorage.setItem('branch_id', branch._id);
    localStorage.setItem('secure_branch_id', branch._id);
  };

  const clearBranch = () => {
    setSelectedBranch(null);
    localStorage.removeItem('selectedBranch');
    localStorage.removeItem('branch_id');
    localStorage.removeItem('secure_branch_id');
  };

  const value = {
    selectedBranch,
    branches,
    loading,
    selectBranch,
    clearBranch,
    fetchBranches
  };

  return (
    <BranchContext.Provider value={value}>
      {children}
    </BranchContext.Provider>
  );
};

